import { useState } from 'react';
import { useRouter } from 'next/router';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { SortBy } from 'components/common';
import { DropdownSelectable } from 'components/common/dropdown';
import { Filters, FiltersActive } from 'components/common/filter';
import { UsersSearch } from 'components/users';

import type { CompetencyMatrixView } from './CompetencyMatrix';

interface Props {
  allowedColumnIds?: Record<'competenceProfiles' | 'competences', number[]>;
  setColumnsLoading: React.Dispatch<React.SetStateAction<any>>;
  setUsersLoading: React.Dispatch<React.SetStateAction<any>>;
  view?: CompetencyMatrixView;
}

const CompetencyMatrixSubHeader = ({
  allowedColumnIds,
  setColumnsLoading,
  setUsersLoading,
  view = 'competencies'
}: Props) => {
  const { messages } = useReduxState();

  const router = useRouter();

  const [usersPerPage, setUsersPerPage] = useState<number>(
    parseInt(router.query?.usersPerPage as string, 10) || 25
  );

  const handleUsersNumberChange = async (number: number) => {
    setUsersLoading(true);
    setUsersPerPage(number);

    const query = { ...router.query, page: 1, usersPerPage: number };
    router.replace({ query }, { query }, { shallow: true });
  };

  const handleViewChange = async (view: 'competencies' | 'profiles') => {
    setColumnsLoading(true);
    const query = { ...router.query, view };
    router.replace({ query }, { query }, { shallow: true });
  };

  return (
    <>
      <div className="sub-header">
        <div className="filters">
          <div className="search">
            <UsersSearch
              onSearchOrClear={() => setUsersLoading(true)}
              size="header"
            />
          </div>

          <Filters
            allowMultiple={{
              accountAccess: true,
              accountIdentifier: true,
              accountLogin: true,
              accountType: true,
              achievements: true,
              ambitions: true,
              calendarEvents: true,
              competenceProfiles: true,
              competences: true,
              courses: true,
              groups: true,
              paths: true,
              supervisors: true,
              userAttributes: true
            }}
            filterId="users"
            filters={{
              accountAccess: true,
              accountIdentifier: true,
              accountLogin: true,
              accountType: {
                items: [
                  {
                    label: messages.commonUser,
                    value: 'user'
                  },
                  {
                    label: messages.usersAdmin,
                    value: 'admin'
                  },
                  {
                    label: messages.usersSuperadmin,
                    value: 'superadmin'
                  },
                  {
                    label: messages.commonUserExclude,
                    value: 'excludeUser'
                  },
                  {
                    label: messages.usersAdminExclude,
                    value: 'excludeAdmin'
                  },
                  {
                    label: messages.usersSuperadminExclude,
                    value: 'excludeSuperadmin'
                  }
                ]
              },
              achievements: true,
              ambitions: true,
              calendarEvents: true,
              competenceProfiles: true,
              competences: true,
              courses: true,
              groups: true,
              paths: true,
              supervisors: true,
              userAttributes: true
            }}
            triggerProps={{ text: messages.commonFiltersUsers }}
          />

          <Filters
            allowedItemIds={allowedColumnIds}
            allowMultiple={{ competenceProfiles: true, competences: true }}
            filterId="columns"
            filters={{
              competenceProfiles: { field: 'profilesInColumns' },
              competences: { field: 'competencesInColumns' }
            }}
            triggerProps={{
              icon: null,
              text: messages.competencyMatrixSelectColumns
            }}
          />
        </div>

        <div className="view">
          <DropdownSelectable
            category="lighter"
            defaultText={`${messages.commonSelect}...`}
            defaultValue={view}
            dropdownTextMinWidth="120px"
            onChange={handleViewChange}
            options={[
              { text: messages.analyticsCompetencies, value: 'competencies' },
              { text: messages.competenciesLevels, value: 'competency-levels' },
              { text: messages.competencyProfiles, value: 'profiles' }
            ]}
            selectedValueBefore={`${messages.commonView}:`}
            showSelectedText={true}
            size="header"
            titleMaxWidth={120}
            value={view}
          />

          <DropdownSelectable
            category="lighter"
            defaultText={`${messages.commonSelect}...`}
            defaultValue={usersPerPage}
            dropdownTextMinWidth="120px"
            onChange={handleUsersNumberChange}
            options={[
              {
                text: getMessageWithPlaceholders({
                  message: messages.usersNumberOfUsers,
                  placeholders: { numberOfUsers: '10' }
                }),
                value: 10
              },
              {
                text: getMessageWithPlaceholders({
                  message: messages.usersNumberOfUsers,
                  placeholders: { numberOfUsers: '25' }
                }),
                value: 25
              },
              {
                text: getMessageWithPlaceholders({
                  message: messages.usersNumberOfUsers,
                  placeholders: { numberOfUsers: '50' }
                }),
                value: 50
              }
            ]}
            selectedValueBefore={`${messages.commonShow}:`}
            showSelectedText={true}
            size="header"
            titleMaxWidth={120}
            value={usersPerPage}
          />

          <SortBy
            onChange={({ sortBy }) => {
              setUsersLoading(true);
              router.replace({ query: { ...router.query, sortBy } });
            }}
            options={[
              { text: messages.commonAlphabetic, value: 'alphabetical' },
              { text: messages.commonUserCompetencies, value: 'competencies' }
            ]}
            value={(router.query.sortBy as string) || 'alphabetical'}
          />
        </div>
      </div>

      <FiltersActive filterId="users" />

      <style jsx>{`
        .sub-header {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: space-between;
          width: 100%;
        }
        .search {
          width: 240px;
        }
        .filters {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .view {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
      `}</style>
    </>
  );
};

export default CompetencyMatrixSubHeader;
